/**
 * Theme Settings Applicator
 * Maps theme_customization_settings from the backend to CSS custom properties
 * for the v3 template (smokeandmirrors theme).
 */

/**
 * Convert hex color to RGB string (e.g., "#d4a84b" -> "212, 168, 75")
 * @param {string} hex - Hex color string
 * @returns {string|null} RGB values as comma-separated string, or null if invalid
 */
function hexToRgb(hex) {
  if (!hex) return null;
  // Remove # if present
  hex = hex.replace(/^#/, '');
  // Handle shorthand hex (e.g., #abc -> #aabbcc)
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  if (hex.length !== 6) return null;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  return `${r}, ${g}, ${b}`;
}

/**
 * Apply theme customization settings as CSS custom properties
 * @param {Object} settings - The theme_customization_settings object from userSettings
 */
export function applyThemeSettings(settings) {
  if (!settings) return;
  const root = document.documentElement;

  // COLORS
  if (settings.colors) {
    const c = settings.colors;
    if (c.primary) {
      root.style.setProperty('--sm-accent', c.primary);
      // Also set the RGB version for shadow/glow variables
      const primaryRgb = hexToRgb(c.primary);
      if (primaryRgb) {
        root.style.setProperty('--sm-accent-rgb', primaryRgb);
      }
    }
    if (c.secondary) root.style.setProperty('--sm-accent-dark', c.secondary);
    if (c.accent) root.style.setProperty('--sm-accent-hover', c.accent);
    if (c.background) root.style.setProperty('--sm-bg-primary', c.background);
    if (c.text_primary) root.style.setProperty('--sm-text-primary', c.text_primary);
    if (c.text_secondary) root.style.setProperty('--sm-text-secondary', c.text_secondary);
    if (c.border) root.style.setProperty('--sm-border-color', c.border);
    if (c.error) root.style.setProperty('--sm-error', c.error);
    if (c.success) root.style.setProperty('--sm-success', c.success);
    if (c.warning) root.style.setProperty('--sm-warning', c.warning);
    if (c.overlay) root.style.setProperty('--sm-overlay', c.overlay);

    // Gradient colors
    if (c.gradient_start) root.style.setProperty('--sm-gradient-start', c.gradient_start);
    if (c.gradient_end) root.style.setProperty('--sm-gradient-end', c.gradient_end);
  }

  // FONTS
  if (settings.fonts) {
    const f = settings.fonts;
    if (f.heading?.family) {
      const headingFont = f.heading.custom_url
        ? f.heading.family
        : `${f.heading.family}, ${f.heading.fallback || 'Arial, sans-serif'}`;
      root.style.setProperty('--sm-font-heading', headingFont);
      if (f.heading.weight) root.style.setProperty('--sm-font-heading-weight', f.heading.weight);
    }
    if (f.body?.family) {
      const bodyFont = f.body.custom_url
        ? f.body.family
        : `${f.body.family}, ${f.body.fallback || 'Arial, sans-serif'}`;
      root.style.setProperty('--sm-font-body', bodyFont);
      if (f.body.weight) root.style.setProperty('--sm-font-body-weight', f.body.weight);
    }
    if (f.navigation?.family) {
      root.style.setProperty('--sm-font-nav', `${f.navigation.family}, ${f.navigation.fallback || 'Arial, sans-serif'}`);
    }
    if (f.button?.family) {
      root.style.setProperty('--sm-font-button', `${f.button.family}, ${f.button.fallback || 'Arial, sans-serif'}`);
    }
    if (f.sizes?.base) root.style.setProperty('--sm-font-size-base', f.sizes.base);
    if (f.sizes?.scale) root.style.setProperty('--sm-font-scale', f.sizes.scale);
  }

  // LAYOUT
  if (settings.layout) {
    const l = settings.layout;
    if (l.container_width) root.style.setProperty('--sm-container-width', l.container_width);
    if (l.product_grid_columns) root.style.setProperty('--sm-grid-columns', l.product_grid_columns);
    if (l.mobile_grid_columns) root.style.setProperty('--sm-mobile-grid-columns', l.mobile_grid_columns);
  }

  // BUTTONS
  if (settings.buttons) {
    const b = settings.buttons;
    if (b.border_radius) root.style.setProperty('--sm-button-radius', b.border_radius);
    if (b.size === 'small') {
      root.style.setProperty('--sm-button-padding', '6px 12px');
    } else if (b.size === 'large') {
      root.style.setProperty('--sm-button-padding', '12px 24px');
    } else {
      root.style.setProperty('--sm-button-padding', '8px 16px');
    }
    if (b.uppercase) root.style.setProperty('--sm-button-transform', 'uppercase');
    if (b.shadow) root.style.setProperty('--sm-button-shadow', '0 2px 4px rgba(0,0,0,0.2)');
  }

  // PRODUCT DISPLAY - Rarity colors
  if (settings.product_display?.rarity_color_scheme) {
    const r = settings.product_display.rarity_color_scheme;
    if (r.common) root.style.setProperty('--sm-rarity-common', r.common);
    if (r.uncommon) root.style.setProperty('--sm-rarity-uncommon', r.uncommon);
    if (r.rare) root.style.setProperty('--sm-rarity-rare', r.rare);
    if (r.mythic) root.style.setProperty('--sm-rarity-mythic', r.mythic);
    if (r.special) root.style.setProperty('--sm-rarity-special', r.special);
  }

  // BRANDING
  if (settings.branding) {
    const br = settings.branding;
    if (br.logo_max_width) root.style.setProperty('--sm-logo-max-width', br.logo_max_width);
    if (br.logo_max_height) root.style.setProperty('--sm-logo-max-height', br.logo_max_height);
  }

  // ACCESSIBILITY
  if (settings.accessibility?.high_contrast_mode) {
    root.classList.add('high-contrast');
  } else {
    root.classList.remove('high-contrast');
  }

  // ADVANCED - Custom CSS
  if (settings.advanced?.custom_css) {
    let customStyleEl = document.getElementById('theme-custom-css');
    if (!customStyleEl) {
      customStyleEl = document.createElement('style');
      customStyleEl.id = 'theme-custom-css';
      document.head.appendChild(customStyleEl);
    }
    customStyleEl.textContent = settings.advanced.custom_css;
  }
}

/**
 * Remove all applied theme settings (cleanup)
 */
export function clearThemeSettings() {
  const root = document.documentElement;

  // List of all CSS custom properties we set
  const props = [
    // Colors
    '--sm-accent', '--sm-accent-dark', '--sm-accent-hover', '--sm-accent-rgb',
    '--sm-bg-primary', '--sm-text-primary', '--sm-text-secondary', '--sm-border-color',
    '--sm-error', '--sm-success', '--sm-warning', '--sm-overlay',
    '--sm-gradient-start', '--sm-gradient-end',
    // Fonts
    '--sm-font-heading', '--sm-font-heading-weight', '--sm-font-body', '--sm-font-body-weight',
    '--sm-font-nav', '--sm-font-button', '--sm-font-size-base', '--sm-font-scale',
    // Layout
    '--sm-container-width', '--sm-grid-columns', '--sm-mobile-grid-columns',
    // Buttons
    '--sm-button-radius', '--sm-button-padding', '--sm-button-transform', '--sm-button-shadow',
    // Rarity
    '--sm-rarity-common', '--sm-rarity-uncommon', '--sm-rarity-rare',
    '--sm-rarity-mythic', '--sm-rarity-special',
    // Branding
    '--sm-logo-max-width', '--sm-logo-max-height'
  ];

  props.forEach(p => root.style.removeProperty(p));
  root.classList.remove('high-contrast');

  // Remove custom CSS if present
  const customStyleEl = document.getElementById('theme-custom-css');
  if (customStyleEl) {
    customStyleEl.remove();
  }
}
